import { FC, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';

import { logError } from 'services/logging/logging';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { PartnerV2 } from 'support/types';
import { WebEDIRowData } from 'components/DataTable/Types';
import { FileInput } from 'components/Form/FileInput/FileInput';

import {
  extractEmbeddedTableGroups,
  extractExpectedDataFormats,
  extractFlattenedHeaders,
  extractMainColumns,
} from './columnUtils';
import { dataTransformer } from './dataTransformer/dataTransformer';
import { FileUploadError, UploadValidationError } from './helpers';
import { createLoaderForColumns } from './loader';
import { PriceCatalogUploadErrorDisplay } from './PriceCatalogUploadErrorDisplay/PriceCatalogUploadErrorDisplay';
import { useErrorHandler } from './useErrorHandler';
import { validator } from './validator';

export type XLSXUploaderProps = {
  columns: Array<ColumnDef<WebEDIRowData, unknown>>;
  relationshipId: string;
  onLoad: (data: Array<Record<string, unknown>>, filename: string) => void;
  receiverPartner: PartnerV2;
  onDownloadTemplate: () => void;
};
export const PriceCatalogXLSXUploader: FC<XLSXUploaderProps> = ({
  columns,
  onLoad,
  relationshipId,
  receiverPartner,
  onDownloadTemplate,
}) => {
  const [uploadError, setUploadError] = useState<FileUploadError>();
  const flattenedHeaders = useMemo(() => extractFlattenedHeaders(columns), [columns]);
  const formats = useMemo(() => extractExpectedDataFormats(columns), [columns]);
  const mainColumns = useMemo(() => extractMainColumns(columns), [columns]);
  const embeddedTableGroups = useMemo(() => extractEmbeddedTableGroups(columns), [columns]);
  const loader = useMemo(() => createLoaderForColumns(columns), [columns]);
  const { track } = useAnalytics();
  const { handleOnError } = useErrorHandler({ relationshipId, setUploadError, track });

  const handleOnLoad = (data: Record<string, Array<Record<string, unknown>>>, file: File) => {
    let sheetName = undefined;

    const isValid = validator({
      expectedData: { headers: flattenedHeaders, formats },
      setSheetName: (name) => (sheetName = name),
      data,
    });

    if (isValid.success === false) {
      handleOnError(new UploadValidationError(isValid.errorCode, isValid.columns || [], isValid.errorDetails), file);
      return;
    }

    setUploadError(undefined);
    if (!sheetName) {
      logError('Sheet name is undefined. Cannot transform data.');
      setUploadError({ code: 'UNKNOWN' });
      return;
    }

    track('webedi:pricat_uploaded_template.successful', {
      filename: file.name,
    });

    const transformedData = dataTransformer(data, sheetName, { mainColumns, embeddedTableGroups }, formats);
    onLoad(transformedData, file.name);
  };

  return (
    <div className="flex flex-col gap-4">
      <FileInput
        accept={{
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        }}
        onLoad={handleOnLoad}
        onError={handleOnError}
        loader={loader}
        multiple={false}
      />
      {uploadError && (
        <PriceCatalogUploadErrorDisplay
          uploadError={uploadError}
          receiverPartner={receiverPartner}
          columns={columns}
          onDownloadTemplate={onDownloadTemplate}
        />
      )}
    </div>
  );
};
